"use client";

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  executeRelayedStopWorkflow,
  routeStopWorkflowRequest,
  useAutodialerSessionChannel,
  type RelayedActionHandlerResult,
  type RelayedStopWorkflowPayload,
  type RelayedWorkflowAction,
  type StopWorkflowResult,
} from "@/app/_components/adminWorkflows/autodialerSessionChannel";
import { shouldUpgradeCallWindowOnOwnershipPromotion } from "@/app/_components/adminWorkflows/call-window-ownership-promotion";
import { getStoredAutodialerCallWindowId } from "@/app/_components/adminWorkflows/autodialerCallChannel";
import { showWorkflowSessionSummaryToast } from "@/app/_components/adminWorkflows/showWorkflowSessionSummaryToast";
import { useAutodialerCallWindow } from "@/app/_components/adminWorkflows/useAutodialerCallWindow";
import { useWorkflowOrchestration } from "@/app/_components/adminWorkflows/useWorkflowOrchestration";
import { applyStopWorkflowResult } from "@/app/_components/adminWorkflows/workflow-stop/utils";
import { workflowReducer } from "@/app/_components/adminWorkflows/workflowReducer";
import {
  requestVoipCallPermissionsFromUserGesture,
  watchVoipMicrophonePermission,
} from "@/app/_components/calls/voice-call/voipPermissions";
import { trackDataDogAction } from "@/lib/analytics/trackDataDogAction";
import type {
  CallWorkflowContextValue,
  CallWorkflowState,
  StopWorkflowOptions,
  WorkflowQueueItem,
  WorkflowReducerState,
  WorkflowVoiceCallStartResult,
} from "@/lib/modules/calls/workflow/types";
import { INITIAL_WORKFLOW_STATE } from "@/lib/modules/calls/workflow/types";
import {
  isWorkflowCallInProgress,
  resolveActiveWorkflowTaskId,
  workflowSessionSummaryStatsToAnalyticsContext,
} from "@/lib/modules/calls/workflow/utils";
import { useTRPC } from "@/trpc/client";
import { useQueryClient } from "@tanstack/react-query";
import getLogger from "@/lib/logger";
import { toast } from "sonner";

const logger = getLogger("CallWorkflowContext");

export const CallWorkflowContext =
  createContext<CallWorkflowContextValue | null>(null);

interface CallWorkflowProviderProps {
  children: React.ReactNode;
  /** Admin's personal phone number for manual call fallback. */
  agentPhoneNumber?: string | null;
  /** Admin table ID (authUserInfo.userTypeId) for Twilio + call log. */
  adminId?: number | null;
  /** Auth user id — keys the cross-tab session BroadcastChannel. */
  userId?: number | null;
}

export function CallWorkflowProvider({
  children,
  agentPhoneNumber = null,
  adminId = null,
  userId = null,
}: CallWorkflowProviderProps) {
  const [state, dispatch] = useReducer(workflowReducer, INITIAL_WORKFLOW_STATE);
  const [isOutboundCallerIdReady, setOutboundCallerIdReady] =
    React.useState(false);
  const [isCallLogSubmitting, setIsCallLogSubmitting] = React.useState(false);
  const [isSessionOwner, setIsSessionOwner] = useState(false);
  const skipCurrentTaskHandlerRef = useRef<(() => Promise<void>) | null>(null);
  const startVoiceCallHandlerRef = useRef<
    (() => Promise<WorkflowVoiceCallStartResult>) | null
  >(null);
  const endActiveCallHandlerRef = useRef<
    (() => Promise<string | null | void>) | null
  >(null);
  const householdWrapUpSubmitRef = useRef<(() => Promise<boolean>) | null>(
    null,
  );
  const [householdWrapUpPending, setHouseholdWrapUpPending] = useState(false);
  const stopWorkflowAsOwnerRef = useRef<
    ((options?: StopWorkflowOptions) => Promise<StopWorkflowResult>) | null
  >(null);
  const isSessionOwnerRef = useRef(isSessionOwner);

  useEffect(() => {
    isSessionOwnerRef.current = isSessionOwner;
  }, [isSessionOwner]);

  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const callWindow = useAutodialerCallWindow(userId);

  useEffect(() => {
    void watchVoipMicrophonePermission();
    callWindow.reattachIfPresent({ probeNamedWindow: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Once this tab wins session ownership (e.g. after reclaiming a session on
  // reload), upgrade the listen-only binding above to a full attach so it can
  // detect the popup closing and keep the channel alive/repaired for the rest
  // of any call already in progress.
  const wasSessionOwnerRef = useRef(isSessionOwner);
  useEffect(() => {
    const becameOwner = isSessionOwner && !wasSessionOwnerRef.current;
    wasSessionOwnerRef.current = isSessionOwner;
    if (becameOwner && userId != null) {
      const storedId = getStoredAutodialerCallWindowId(userId);
      if (
        shouldUpgradeCallWindowOnOwnershipPromotion({
          becameOwner,
          hasStoredWindow: storedId != null,
          isDrivingCallWindow: callWindow.isDrivingCallWindow,
        })
      ) {
        callWindow.reattachIfPresent();
      }
    }
  }, [isSessionOwner, callWindow, userId]);

  const sessionTeardownRef = useRef<
    (payload?: RelayedStopWorkflowPayload) => void
  >(() => {});
  const ownershipRecoveryRef = useRef<() => void>(() => {});

  const {
    startWorkflow: startWorkflowAction,
    stopWorkflow,
    skipTask,
    unskipTask,
    transitionState,
    advanceQueue,
    advanceQueueAfterMistakeCancel,
    recordCompletedCallInSession,
    revertCompletedCallFromSession,
    skipCurrentAndAdvance,
    takeScheduledCallNow,
    continuePastScheduledPrompt,
    clearPendingTaskNavigation,
    clearLocalWorkflow,
    markOwnerSyncReceived,
  } = useWorkflowOrchestration({
    state,
    dispatch,
    isSessionOwner,
    setIsSessionOwner,
    onSessionCleared: (payload) => sessionTeardownRef.current(payload),
    onOwnershipConflict: () => ownershipRecoveryRef.current(),
  });

  const handleRelayedAction = useCallback(
    async (
      action: RelayedWorkflowAction,
    ): Promise<RelayedActionHandlerResult> => {
      try {
        switch (action.type) {
          case "skipTask":
            await skipTask(action.taskId);
            break;
          case "unskipTask":
            await unskipTask(action.taskId);
            break;
          case "skipCurrentTask": {
            const handler = skipCurrentTaskHandlerRef.current;
            if (handler) {
              await handler();
              break;
            }
            const taskId = resolveActiveWorkflowTaskId({
              currentTaskId: state.currentTaskId,
              queue: state.queue,
              queuePosition: state.queuePosition,
            });
            if (taskId != null) {
              await skipCurrentAndAdvance(taskId);
            }
            break;
          }
          case "skipCurrentAndAdvance":
            await skipCurrentAndAdvance(action.taskId);
            break;
          case "advanceQueue":
            await advanceQueue();
            break;
          case "advanceQueueAfterMistakeCancel":
            await advanceQueueAfterMistakeCancel();
            break;
          case "takeScheduledCallNow":
            await takeScheduledCallNow(action.taskId);
            break;
          case "continuePastScheduledPrompt":
            await continuePastScheduledPrompt();
            break;
          case "startVoiceCall": {
            const result =
              (await startVoiceCallHandlerRef.current?.()) ??
              "retryable_failure";
            if (result === "completed") {
              transitionState("CONNECTING");
            }
            return { status: result };
          }
          case "transitionState":
            transitionState(action.state);
            break;
          case "recordCompletedCallInSession":
            recordCompletedCallInSession({
              isConverted: action.isConverted,
              completedTaskIds: action.completedTaskIds,
              convertedDelta: action.convertedDelta,
              countsAsDial: action.countsAsDial,
            });
            break;
          case "revertCompletedCallFromSession":
            revertCompletedCallFromSession({
              wasConverted: action.wasConverted,
              convertedDelta: action.convertedDelta,
              completedTaskIds: action.completedTaskIds,
              skippedTaskIds: action.skippedTaskIds,
              countsAsDial: action.countsAsDial,
              removedScheduledTasks: action.removedScheduledTasks,
            });
            break;
          case "markCallLogSaved":
            dispatch({ type: "SET_CALL_LOG_SAVED", payload: { saved: true } });
            break;
          case "incrementConverted":
            dispatch({ type: "INCREMENT_CONVERTED" });
            break;
          case "stopWorkflow": {
            const stopAsOwner = stopWorkflowAsOwnerRef.current;
            if (!stopAsOwner) {
              return { status: "retryable_failure" };
            }
            return executeRelayedStopWorkflow(action, stopAsOwner);
          }
        }
      } catch (error) {
        // Terminal, not retryable. Returning nothing here would leave the
        // action unsettled, and the requesting tab would keep re-relaying it
        // every retry tick for the rest of the session.
        logger.warn(
          { error, actionType: action.type },
          "Relayed workflow action failed on the owner tab",
        );
        return { status: "terminal_failure" };
      }
    },
    [
      advanceQueue,
      advanceQueueAfterMistakeCancel,
      continuePastScheduledPrompt,
      recordCompletedCallInSession,
      revertCompletedCallFromSession,
      skipCurrentAndAdvance,
      skipTask,
      state.currentTaskId,
      state.queue,
      state.queuePosition,
      takeScheduledCallNow,
      transitionState,
      unskipTask,
    ],
  );

  const handleSyncedState = useCallback(
    (synced: WorkflowReducerState) => {
      markOwnerSyncReceived();
      dispatch({ type: "APPLY_SYNCED_STATE", payload: { state: synced } });
    },
    [markOwnerSyncReceived],
  );

  const handleRemoteSessionEnded = useCallback(() => {
    clearLocalWorkflow();
    callWindow.close();
  }, [callWindow, clearLocalWorkflow]);

  const handlePrepCache = useCallback(
    (studentId: number, data: unknown) => {
      // Data arrives via BroadcastChannel (structured clone) — same shape as
      // the original tRPC response, so the cast is safe.
      queryClient.setQueryData(
        trpc.callWorkflow.getSessionPrep.queryKey({ studentId }),
        data as never,
      );
    },
    [queryClient, trpc],
  );

  const {
    relayAction,
    relayActionAndAwait,
    broadcastSessionEnded,
    broadcastPrepCache,
    probeForOwner,
  } = useAutodialerSessionChannel({
    userId,
    enabled: state.sessionId !== null && userId != null,
    isSessionOwner,
    setIsSessionOwner,
    state,
    onRelayedAction: handleRelayedAction,
    onSessionEnded: handleRemoteSessionEnded,
    onSyncedState: handleSyncedState,
    onPrepCache: handlePrepCache,
  });

  useEffect(() => {
    ownershipRecoveryRef.current = probeForOwner;
  }, [probeForOwner]);

  useEffect(() => {
    sessionTeardownRef.current = (payload) => {
      broadcastSessionEnded(payload);
      callWindow.close();
    };
  }, [broadcastSessionEnded, callWindow]);

  const showPanel = React.useCallback(() => {
    dispatch({ type: "SET_PANEL_VISIBILITY", payload: { visible: true } });
  }, []);

  const hidePanel = React.useCallback(() => {
    dispatch({ type: "SET_PANEL_VISIBILITY", payload: { visible: false } });
  }, []);

  const incrementConverted = React.useCallback(() => {
    if (!isSessionOwnerRef.current) {
      relayAction({ type: "incrementConverted" });
      return;
    }
    dispatch({ type: "INCREMENT_CONVERTED" });
  }, [relayAction]);

  const markCallLogSaved = React.useCallback(() => {
    if (!isSessionOwnerRef.current) {
      relayAction({ type: "markCallLogSaved" });
      return;
    }
    dispatch({ type: "SET_CALL_LOG_SAVED", payload: { saved: true } });
  }, [relayAction]);

  const setQueuePosition = React.useCallback((position: number | null) => {
    dispatch({ type: "SET_QUEUE_POSITION", payload: { position } });
  }, []);
  const setCurrentTaskId = React.useCallback((taskId: number | null) => {
    dispatch({ type: "SET_CURRENT_TASK_ID", payload: { taskId } });
  }, []);

  const registerSkipCurrentTaskHandler = useCallback(
    (handler: (() => Promise<void>) | null) => {
      skipCurrentTaskHandlerRef.current = handler;
    },
    [],
  );

  const registerStartVoiceCallHandler = useCallback(
    (handler: (() => Promise<WorkflowVoiceCallStartResult>) | null) => {
      startVoiceCallHandlerRef.current = handler;
    },
    [],
  );

  const startVoiceCall = useCallback(async () => {
    if (!isSessionOwnerRef.current) {
      // The follower owns the user gesture, so it must open a closed popup
      // and request VoIP permissions before relaying. Do not raise an
      // already-open call window — `window.open` on a named tab would steal
      // focus from prep. The owner retries the action until its bridge sees it.
      const permissionPromise = requestVoipCallPermissionsFromUserGesture();
      if (callWindow.attachStatus !== "attached") {
        callWindow.open();
      }
      const permission = await permissionPromise;
      if (!permission.granted) {
        toast.error(permission.message);
        return false;
      }
      relayAction({ type: "startVoiceCall" });
      // Return false so the footer doesn't transition to CONNECTING until the
      // owner confirms the call actually started. The relay handler will
      // propagate transitionState("CONNECTING") once the owner succeeds.
      return false;
    }

    const result = await startVoiceCallHandlerRef.current?.();
    return result === "completed";
  }, [callWindow, relayAction]);

  const registerEndActiveCallHandler = useCallback(
    (handler: (() => Promise<string | null | void>) | null) => {
      endActiveCallHandlerRef.current = handler;
    },
    [],
  );

  const registerHouseholdWrapUpSubmitHandler = useCallback(
    (handler: (() => Promise<boolean>) | null) => {
      householdWrapUpSubmitRef.current = handler;
      setHouseholdWrapUpPending(handler != null);
    },
    [],
  );

  const submitHouseholdWrapUp = useCallback(async () => {
    const handler = householdWrapUpSubmitRef.current;
    if (handler == null) {
      return true;
    }
    return handler();
  }, []);

  const stopWorkflowAsOwner = useCallback(
    async (options?: StopWorkflowOptions): Promise<StopWorkflowResult> => {
      const endHandler = endActiveCallHandlerRef.current;
      return stopWorkflow(options, async () => {
        if (endHandler) {
          try {
            await endHandler();
          } catch (error) {
            logger.warn(
              { error },
              "Failed to end active call after stopping workflow",
            );
          }
        } else if (
          isWorkflowCallInProgress(state.state) &&
          state.currentTaskId != null
        ) {
          // No call-card is mounted to run its own teardown — e.g. the owner
          // navigated away from the call mid-session, or a follower relayed
          // this stop. Once the version-guarded stop succeeds, hang up the
          // popup call directly so it is not left orphaned.
          try {
            await callWindow.hangUp(state.currentTaskId);
          } catch (error) {
            logger.warn(
              { error },
              "Failed to hang up orphaned call after stopping workflow",
            );
          }
        }
      });
    },
    [callWindow, state.currentTaskId, state.state, stopWorkflow],
  );

  useEffect(() => {
    stopWorkflowAsOwnerRef.current = stopWorkflowAsOwner;
    return () => {
      stopWorkflowAsOwnerRef.current = null;
    };
  }, [stopWorkflowAsOwner]);

  const stopWorkflowOrRelay = useCallback(
    async (options?: StopWorkflowOptions): Promise<boolean> => {
      const result = isSessionOwnerRef.current
        ? await stopWorkflowAsOwner(options)
        : await routeStopWorkflowRequest({
            isOwner: false,
            options,
            relayAndAwait: relayActionAndAwait,
            stopAsOwner: stopWorkflowAsOwner,
          });

      applyStopWorkflowResult({
        result,
        options,
        trackStoppedAction: (summaryStats) =>
          trackDataDogAction(
            "autodialer:workflow:stopped",
            workflowSessionSummaryStatsToAnalyticsContext(summaryStats),
          ),
        trackInactivityTimeoutAction: (summaryStats) =>
          trackDataDogAction(
            "autodialer:workflow:inactivity_timeout",
            workflowSessionSummaryStatsToAnalyticsContext(summaryStats),
          ),
        showSummaryToast: showWorkflowSessionSummaryToast,
        showInfoToast: (message) => toast.info(message),
        showErrorToast: (message) => toast.error(message),
      });

      return result.stopped;
    },
    [relayActionAndAwait, stopWorkflowAsOwner],
  );

  const startWorkflow = useCallback(async (): Promise<boolean> => {
    const storedCallWindowId =
      userId != null ? getStoredAutodialerCallWindowId(userId) : null;
    // Start getUserMedia in this click before any await so the browser will
    // prompt if microphone/audio is not already granted. window.open must
    // also stay in this turn or the popup is blocked.
    const permissionPromise = requestVoipCallPermissionsFromUserGesture();
    if (storedCallWindowId) {
      callWindow.reattachIfPresent({ probeNamedWindow: false });
    } else {
      callWindow.open();
    }
    const permission = await permissionPromise;
    if (!permission.granted) {
      logger.warn(
        { microphone: permission.microphone },
        "Autodialer VoIP permissions were not granted",
      );
      toast.error(permission.message);
      if (!storedCallWindowId) {
        callWindow.close();
      }
      return false;
    }
    const started = await startWorkflowAction();
    if (
      !started &&
      !(userId != null && getStoredAutodialerCallWindowId(userId))
    ) {
      callWindow.close();
    }
    return started;
  }, [callWindow, startWorkflowAction, userId]);

  const skipCurrentTask = useCallback(async () => {
    if (!isSessionOwnerRef.current) {
      relayAction({ type: "skipCurrentTask" });
      return;
    }

    const taskId = resolveActiveWorkflowTaskId({
      currentTaskId: state.currentTaskId,
      queue: state.queue,
      queuePosition: state.queuePosition,
    });
    if (taskId === null) {
      return;
    }

    const handler = skipCurrentTaskHandlerRef.current;
    if (handler) {
      await handler();
      return;
    }

    await skipCurrentAndAdvance(taskId);
  }, [
    relayAction,
    skipCurrentAndAdvance,
    state.currentTaskId,
    state.queue,
    state.queuePosition,
  ]);

  const skipTaskOrRelay = useCallback(
    (taskId: number) => {
      if (!isSessionOwnerRef.current) {
        relayAction({ type: "skipTask", taskId });
        return;
      }
      void skipTask(taskId).catch(() => undefined);
    },
    [relayAction, skipTask],
  );

  const unskipTaskOrRelay = useCallback(
    (taskId: number) => {
      if (!isSessionOwnerRef.current) {
        relayAction({ type: "unskipTask", taskId });
        return;
      }
      void unskipTask(taskId).catch(() => undefined);
    },
    [relayAction, unskipTask],
  );

  const skipCurrentAndAdvanceOrRelay = useCallback(
    async (taskId: number) => {
      if (!isSessionOwnerRef.current) {
        relayAction({ type: "skipCurrentAndAdvance", taskId });
        return;
      }
      await skipCurrentAndAdvance(taskId);
    },
    [relayAction, skipCurrentAndAdvance],
  );

  const advanceQueueOrRelay = useCallback(async () => {
    if (!isSessionOwnerRef.current) {
      relayAction({ type: "advanceQueue" });
      return false;
    }
    return advanceQueue();
  }, [advanceQueue, relayAction]);

  const advanceQueueAfterMistakeCancelOrRelay = useCallback(async () => {
    if (!isSessionOwnerRef.current) {
      relayAction({ type: "advanceQueueAfterMistakeCancel" });
      return;
    }
    await advanceQueueAfterMistakeCancel();
  }, [advanceQueueAfterMistakeCancel, relayAction]);

  const takeScheduledCallNowOrRelay = useCallback(
    async (taskId: number) => {
      if (!isSessionOwnerRef.current) {
        relayAction({ type: "takeScheduledCallNow", taskId });
        return;
      }
      await takeScheduledCallNow(taskId);
    },
    [relayAction, takeScheduledCallNow],
  );

  const continuePastScheduledPromptOrRelay = useCallback(async () => {
    if (!isSessionOwnerRef.current) {
      relayAction({ type: "continuePastScheduledPrompt" });
      return;
    }
    await continuePastScheduledPrompt();
  }, [continuePastScheduledPrompt, relayAction]);

  const transitionStateOrRelay = useCallback(
    (newState: CallWorkflowState) => {
      if (!isSessionOwnerRef.current) {
        relayAction({ type: "transitionState", state: newState });
        return;
      }
      transitionState(newState);
    },
    [relayAction, transitionState],
  );

  const recordCompletedCallInSessionOrRelay = useCallback(
    (options?: {
      isConverted?: boolean;
      completedTaskIds?: number[];
      convertedDelta?: number;
      countsAsDial?: boolean;
    }): WorkflowQueueItem[] => {
      if (!isSessionOwnerRef.current) {
        relayAction({
          type: "recordCompletedCallInSession",
          isConverted: options?.isConverted,
          completedTaskIds: options?.completedTaskIds,
          convertedDelta: options?.convertedDelta,
          countsAsDial: options?.countsAsDial,
        });
        // Compute removedScheduledTasks from our local (synced) state so
        // that the undo path has something to relay back to the owner.
        const taskIds =
          options?.completedTaskIds ??
          (state.currentTaskId !== null ? [state.currentTaskId] : []);
        return taskIds
          .map((id) => state.scheduledStack.find((t) => t.id === id))
          .filter((t): t is WorkflowQueueItem => t !== undefined);
      }
      return recordCompletedCallInSession(options);
    },
    [
      recordCompletedCallInSession,
      relayAction,
      state.currentTaskId,
      state.scheduledStack,
    ],
  );

  const revertCompletedCallFromSessionOrRelay = useCallback(
    (options?: {
      wasConverted?: boolean;
      convertedDelta?: number;
      completedTaskIds?: number[];
      skippedTaskIds?: number[];
      countsAsDial?: boolean;
      removedScheduledTasks?: WorkflowQueueItem[];
    }) => {
      if (!isSessionOwnerRef.current) {
        relayAction({
          type: "revertCompletedCallFromSession",
          wasConverted: options?.wasConverted,
          convertedDelta: options?.convertedDelta,
          completedTaskIds: options?.completedTaskIds,
          skippedTaskIds: options?.skippedTaskIds,
          countsAsDial: options?.countsAsDial,
          removedScheduledTasks: options?.removedScheduledTasks,
        });
        return;
      }
      revertCompletedCallFromSession(options);
    },
    [relayAction, revertCompletedCallFromSession],
  );

  const contextValue = useMemo<CallWorkflowContextValue>(
    () => ({
      ...state,
      agentPhoneNumber,
      adminId: state.sessionMarbleAdminId ?? adminId,
      isSessionOwner,
      startWorkflow,
      stopWorkflow: stopWorkflowOrRelay,
      skipTask: skipTaskOrRelay,
      unskipTask: unskipTaskOrRelay,
      skipCurrentAndAdvance: skipCurrentAndAdvanceOrRelay,
      skipCurrentTask,
      takeScheduledCallNow: takeScheduledCallNowOrRelay,
      continuePastScheduledPrompt: continuePastScheduledPromptOrRelay,
      startVoiceCall,
      isOutboundCallerIdReady,
      setOutboundCallerIdReady,
      isCallLogSubmitting,
      setIsCallLogSubmitting,
      registerSkipCurrentTaskHandler,
      registerStartVoiceCallHandler,
      registerEndActiveCallHandler,
      householdWrapUpPending,
      registerHouseholdWrapUpSubmitHandler,
      submitHouseholdWrapUp,
      setQueuePosition,
      setCurrentTaskId,
      transitionState: transitionStateOrRelay,
      advanceQueue: advanceQueueOrRelay,
      advanceQueueAfterMistakeCancel: advanceQueueAfterMistakeCancelOrRelay,
      recordCompletedCallInSession: recordCompletedCallInSessionOrRelay,
      revertCompletedCallFromSession: revertCompletedCallFromSessionOrRelay,
      showPanel,
      hidePanel,
      incrementConverted,
      markCallLogSaved,
      callWindow,
      clearPendingTaskNavigation,
      broadcastPrepCache,
    }),
    [
      state,
      agentPhoneNumber,
      adminId,
      isSessionOwner,
      startWorkflow,
      stopWorkflowOrRelay,
      skipTaskOrRelay,
      unskipTaskOrRelay,
      skipCurrentAndAdvanceOrRelay,
      skipCurrentTask,
      takeScheduledCallNowOrRelay,
      continuePastScheduledPromptOrRelay,
      startVoiceCall,
      isOutboundCallerIdReady,
      setOutboundCallerIdReady,
      isCallLogSubmitting,
      setIsCallLogSubmitting,
      registerSkipCurrentTaskHandler,
      registerStartVoiceCallHandler,
      registerEndActiveCallHandler,
      householdWrapUpPending,
      registerHouseholdWrapUpSubmitHandler,
      submitHouseholdWrapUp,
      setQueuePosition,
      setCurrentTaskId,
      transitionStateOrRelay,
      advanceQueueOrRelay,
      advanceQueueAfterMistakeCancelOrRelay,
      recordCompletedCallInSessionOrRelay,
      revertCompletedCallFromSessionOrRelay,
      showPanel,
      hidePanel,
      incrementConverted,
      markCallLogSaved,
      callWindow,
      clearPendingTaskNavigation,
      broadcastPrepCache,
    ],
  );

  return (
    <CallWorkflowContext.Provider value={contextValue}>
      {children}
    </CallWorkflowContext.Provider>
  );
}

export function useCallWorkflow(): CallWorkflowContextValue {
  const context = React.useContext(CallWorkflowContext);
  if (!context) {
    throw new Error(
      "useCallWorkflow must be used within a CallWorkflowProvider",
    );
  }
  return context;
}
